"use client";

import { motion } from "framer-motion";
import { Loader2, BrainCircuit } from "lucide-react";

interface LoadingScreenProps {
  message?: string;
  submessage?: string;
}

export function LoadingScreen({ 
  message = "Synchronizing Terminal", 
  submessage = "Establishing secure neural link and accessing archive" 
}: LoadingScreenProps) {
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#050505] overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />
      
      <div className="relative z-10 text-center space-y-10 max-w-md">
        {/* Central Icon with Pulsing Effect */}
        <div className="relative mx-auto w-28 h-24">
          <motion.div 
            className="absolute inset-0 bg-primary/20 rounded-[2.5rem] blur-2xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="relative flex h-24 w-24 mx-auto items-center justify-center rounded-[2.5rem] bg-[#08080a] border border-primary/30 shadow-[0_0_50px_-12px_rgba(var(--primary),0.5)]">
            <BrainCircuit className="h-10 w-10 text-primary" />
          </div>
          
          {/* Orbiting particles */}
          <motion.div 
            className="absolute top-0 right-0 w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_rgba(var(--primary),0.8)]"
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "-12px 60px" }}
          />
          <motion.div 
            className="absolute bottom-0 left-0 w-1.5 h-1.5 rounded-full bg-primary/60 shadow-[0_0_8px_rgba(var(--primary),0.4)]"
            animate={{ rotate: -360 }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "36px -36px" }}
          />
        </div>

        {/* Textual Feedback */}
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
            <h2 className="text-sm font-black text-white uppercase tracking-[0.4em] ml-1">
              {message}
            </h2>
          </div>
          <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-[0.2em] leading-relaxed max-w-[300px] mx-auto">
            {submessage}
          </p>
        </div>

        {/* Neural Activity Indicators */}
        <div className="flex items-center justify-center gap-10 pt-6">
          <div className="flex flex-col items-center gap-2.5">
            <div className="w-1 h-1 rounded-full bg-primary animate-pulse" />
            <span className="text-[8px] font-black text-zinc-800 uppercase tracking-[0.25em]">Link</span>
          </div>
          <div className="flex flex-col items-center gap-2.5">
            <div className="w-1 h-1 rounded-full bg-primary animate-pulse delay-150" />
            <span className="text-[8px] font-black text-zinc-800 uppercase tracking-[0.25em]">Sync</span>
          </div>
          <div className="flex flex-col items-center gap-2.5">
            <div className="w-1 h-1 rounded-full bg-primary animate-pulse delay-300" />
            <span className="text-[8px] font-black text-zinc-800 uppercase tracking-[0.25em]">Core</span>
          </div>
        </div>
      </div>

      {/* Edge Shimmer */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </div>
  );
}
